import type { CameraControls } from "@react-three/drei";
import { Box3, type Group, type PerspectiveCamera, Vector3 } from "three";

export const CAMERA_FIT_PADDING = {
  paddingLeft: 0.1,
  paddingRight: 0.1,
  paddingBottom: 0.1,
  paddingTop: 0.1,
};

export const ISOMETRIC_CAMERA_DIRECTION: [number, number, number] = [1, 2, 3];

export const INITIAL_FIT_STABLE_FRAMES = 5;

const PERSPECTIVE_FIT_PADDING_FACTOR = 1.1;
const INITIAL_FIT_BOUNDS_EPSILON = 0.0001;

const getBoxCorners = (box: Box3) => [
  new Vector3(box.min.x, box.min.y, box.min.z),
  new Vector3(box.min.x, box.min.y, box.max.z),
  new Vector3(box.min.x, box.max.y, box.min.z),
  new Vector3(box.min.x, box.max.y, box.max.z),
  new Vector3(box.max.x, box.min.y, box.min.z),
  new Vector3(box.max.x, box.min.y, box.max.z),
  new Vector3(box.max.x, box.max.y, box.min.z),
  new Vector3(box.max.x, box.max.y, box.max.z),
];

export const hasPendingLoadingBounds = (object: Group) => {
  let hasPendingBounds = false;
  object.traverse((child) => {
    if (child.name === "__supply-part-loading-bounds") {
      hasPendingBounds = true;
    }
  });

  return hasPendingBounds;
};

export const areBoxesNearlyEqual = (a: Box3, b: Box3) =>
  a.min.distanceToSquared(b.min) <= INITIAL_FIT_BOUNDS_EPSILON &&
  a.max.distanceToSquared(b.max) <= INITIAL_FIT_BOUNDS_EPSILON;

export const waitForNextAnimationFrame = () =>
  new Promise<void>((resolve) => {
    requestAnimationFrame(() => resolve());
  });

export const getObjectBoundingBoxCenter = (object: Group): Vector3 | null => {
  const box = new Box3().setFromObject(object);
  if (box.isEmpty()) {
    return null;
  }

  return box.getCenter(new Vector3());
};

export const fitPerspectiveCameraToBox = async (
  cameraControls: CameraControls,
  camera: PerspectiveCamera,
  object: Group,
  direction: [number, number, number],
  enableTransition = true,
) => {
  const box = new Box3().setFromObject(object);
  if (box.isEmpty()) return;

  const center = box.getCenter(new Vector3());
  const viewDirection = new Vector3(...direction).normalize();
  const forward = viewDirection.clone().negate();
  const worldUp =
    Math.abs(forward.dot(new Vector3(0, 1, 0))) > 0.999
      ? new Vector3(0, 0, 1)
      : new Vector3(0, 1, 0);
  const right = forward.clone().cross(worldUp).normalize();
  const up = right.clone().cross(forward).normalize();
  const tanHalfVerticalFov = Math.tan((camera.fov * Math.PI) / 360);
  const tanHalfHorizontalFov = tanHalfVerticalFov * camera.aspect;

  const distance = getBoxCorners(box).reduce((requiredDistance, corner) => {
    const relativeCorner = corner.sub(center);
    const depthOffset = relativeCorner.dot(viewDirection);
    const horizontalDistance =
      Math.abs(relativeCorner.dot(right)) / tanHalfHorizontalFov + depthOffset;
    const verticalDistance =
      Math.abs(relativeCorner.dot(up)) / tanHalfVerticalFov + depthOffset;

    return Math.max(requiredDistance, horizontalDistance, verticalDistance);
  }, camera.near);

  const paddedDistance = Math.max(
    distance * PERSPECTIVE_FIT_PADDING_FACTOR,
    camera.near,
  );
  const cameraPosition = center
    .clone()
    .add(viewDirection.multiplyScalar(paddedDistance));

  await cameraControls.setLookAt(
    cameraPosition.x,
    cameraPosition.y,
    cameraPosition.z,
    center.x,
    center.y,
    center.z,
    enableTransition,
  );
};
